import { useMemo } from 'react';
import type { JSX } from 'react';
import {
  NewBadgeSetting,
  SettingsEmpty,
  SettingsSection,
  ViewDefaultSetting,
  useSettingsDoor,
} from '@taki/ui';
import type { SettingsDoor } from '@taki/ui';
import type { AppDeclaree } from './app-declaree.js';
import { assemblerEcran } from './assemblage.js';
import type { EcranAssemble } from './assemblage.js';
import { ReglageDemo } from './ReglageDemo.js';
import { jeuxDesInstances, resoudreDeclaration } from './resolution.js';
import { useAppRuntime } from './runtime.js';

/**
 * LES REGLAGES D'UNE APP DECLAREE, montes par le hub Parametres. Une porte :
 * « Mon affichage » (vue d'arrivee, nouveautes). L'app : son jeu de
 * demonstration. Les Étiquettes et listes et le Studio sont poses par le hub.
 */
export function ReglagesDeclares({ app }: { app: AppDeclaree }): JSX.Element | null {
  const door = useSettingsDoor();
  return door === null ? <ReglageDemo app={app} /> : <MonAffichage app={app} door={door} />;
}

function MonAffichage({ app, door }: { app: AppDeclaree; door: SettingsDoor }): JSX.Element {
  const { sdk, instances } = useAppRuntime();
  const resolution = useMemo(() => resoudreDeclaration(app, instances?.[app.id], jeuxDesInstances(instances)), [app, instances]);

  const declaration = useMemo(() => {
    const courante = resolution.declaration;
    const ecran = courante.ecrans.ecrans.find((candidate) => candidate.id === door.screenId);
    return ecran === undefined || ecran === courante.ecran ? courante : { ...courante, ecran };
  }, [resolution.declaration, door.screenId]);

  /* Meme assemblage que l'ecran : les vues indisponibles gardent leur raison. */
  const assemble: EcranAssemble = useMemo(
    () => assemblerEcran(declaration, sdk, app.fonctions, app.effets),
    [declaration, sdk, app.fonctions, app.effets],
  );

  const porte = assemble.portes.find((candidate) => candidate.id === door.doorId);
  const bindings = useMemo(() => assemble.bindings(door.doorId), [assemble, door.doorId]);
  if (door.screenId !== assemble.ecran.id || porte === undefined) {
    return <SettingsEmpty>Cette porte n’est plus disponible.</SettingsEmpty>;
  }
  const types = porte.viewIds === undefined ? assemble.types : assemble.types.filter((type) => porte.viewIds!.includes(type.id));

  return (
    <SettingsSection title="Mon affichage">
      <ViewDefaultSetting
        key={`${assemble.ecran.id}:${door.doorId}`}
        appId={app.id}
        screenId={assemble.ecran.id}
        doorId={door.doorId}
        types={types}
        bindings={bindings}
        label="Ouvrir sur"
        hint="Cette porte"
      />
      <NewBadgeSetting
        appId={app.id}
        screenId={assemble.ecran.id}
        screenIds={declaration.ecrans.ecrans.map((ecran) => ecran.id)}
        label="Nouveautés de l’app"
        hint="Toutes les portes"
      />
    </SettingsSection>
  );
}

export default ReglagesDeclares;
